import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { analyticsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Brain,
    MessageSquare,
    Sparkles,
    TrendingUp,
    Users,
    Target,
    Zap,
    Heart
} from 'lucide-react';
import {
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Cell
} from 'recharts';

const TraitBar = ({ label, value, color }) => (
    <div className="space-y-1">
        <div className="flex justify-between text-sm">
            <span className="text-stone-600">{label}</span>
            <span className="font-mono text-stone-900">{(value * 100).toFixed(0)}%</span>
        </div>
        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${value * 100}%` }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className={`h-full rounded-full ${color}`}
            />
        </div>
    </div>
);

export default function PsychometricsPage() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [textToAnalyze, setTextToAnalyze] = useState('');
    const [context, setContext] = useState('general');
    const [analysisResult, setAnalysisResult] = useState(null);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await analyticsAPI.getPsychometricProfile();
            setProfile(response.data);
        } catch (error) {
            console.error('Failed to fetch profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyze = async () => {
        if (!textToAnalyze.trim()) return;
        
        try {
            setAnalyzing(true);
            const response = await analyticsAPI.analyzeSentiment({
                text: textToAnalyze,
                context
            });
            setAnalysisResult(response.data);
            setTextToAnalyze('');
            // Refresh profile after analysis
            await fetchProfile();
        } catch (error) {
            console.error('Analysis failed:', error);
        } finally {
            setAnalyzing(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500">Loading your profile...</p>
                </div>
            </div>
        );
    }

    const bigFiveData = profile?.big_five ? [
        { trait: 'Openness', value: profile.big_five.openness, fullMark: 1 },
        { trait: 'Conscientiousness', value: profile.big_five.conscientiousness, fullMark: 1 },
        { trait: 'Extraversion', value: profile.big_five.extraversion, fullMark: 1 },
        { trait: 'Agreeableness', value: profile.big_five.agreeableness, fullMark: 1 },
        { trait: 'Neuroticism', value: profile.big_five.neuroticism, fullMark: 1 },
    ] : [];

    const discData = profile?.disc_profile ? [
        { name: 'Dominance', value: profile.disc_profile.dominance, color: '#4F46E5' },
        { name: 'Influence', value: profile.disc_profile.influence, color: '#10B981' },
        { name: 'Steadiness', value: profile.disc_profile.steadiness, color: '#F59E0B' },
        { name: 'Compliance', value: profile.disc_profile.compliance, color: '#F47266' },
    ] : [];

    return (
        <div className="p-8" data-testid="psychometrics-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold font-heading text-stone-900 flex items-center gap-3">
                        <Brain className="w-8 h-8 text-indigo-600" />
                        Psychometric Profile
                    </h1>
                    <p className="text-stone-500 mt-1">
                        AI-powered personality insights based on your communication patterns.
                    </p>
                </div>

                {/* Text Analysis Card */}
                <Card className="mb-6 bento-card">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-indigo-600" />
                            Sentiment Analysis
                        </CardTitle>
                        <CardDescription>
                            Analyze text to understand sentiment and contribute to your psychometric profile.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <Textarea
                                    placeholder="Paste or type text from your emails, feedback, or thoughts..."
                                    value={textToAnalyze}
                                    onChange={(e) => setTextToAnalyze(e.target.value)}
                                    className="min-h-[120px] rounded-xl"
                                    data-testid="analysis-text-input"
                                />
                            </div>
                            <div className="w-48 space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-stone-700 mb-2 block">Context</label>
                                    <Select value={context} onValueChange={setContext}>
                                        <SelectTrigger className="rounded-xl" data-testid="context-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="general">General</SelectItem>
                                            <SelectItem value="appraisal">Appraisal</SelectItem>
                                            <SelectItem value="feedback">Feedback</SelectItem>
                                            <SelectItem value="survey">Survey</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button
                                    onClick={handleAnalyze}
                                    disabled={analyzing || !textToAnalyze.trim()}
                                    className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700"
                                    data-testid="analyze-btn"
                                >
                                    {analyzing ? (
                                        <span className="flex items-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            Analyzing...
                                        </span>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4 mr-2" />
                                            Analyze
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>

                        {analysisResult && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 bg-indigo-50 rounded-xl border border-indigo-100"
                            >
                                <div className="flex items-center gap-4 mb-4">
                                    <Badge className={`capitalize ${
                                        analysisResult.sentiment === 'positive' ? 'bg-emerald-100 text-emerald-800' :
                                        analysisResult.sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                                        'bg-stone-100 text-stone-800'
                                    }`}>
                                        {analysisResult.sentiment}
                                    </Badge>
                                    <span className="text-sm text-stone-600">
                                        Score: <span className="font-mono font-medium">{analysisResult.score?.toFixed(2)}</span>
                                    </span>
                                    <span className="text-sm text-stone-600">
                                        Confidence: <span className="font-mono font-medium">{(analysisResult.confidence * 100).toFixed(0)}%</span>
                                    </span>
                                </div>
                                {analysisResult.key_phrases?.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-stone-700 mb-2">Key Phrases:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {analysisResult.key_phrases.map((phrase, i) => (
                                                <Badge key={i} variant="outline" className="text-xs">
                                                    {phrase}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </CardContent>
                </Card>

                {/* Profile Cards */}
                <Tabs defaultValue="bigfive" className="space-y-6">
                    <TabsList className="bg-stone-100 p-1 rounded-xl">
                        <TabsTrigger value="bigfive" className="rounded-lg">Big Five</TabsTrigger>
                        <TabsTrigger value="disc" className="rounded-lg">DISC Profile</TabsTrigger>
                        <TabsTrigger value="insights" className="rounded-lg">Insights</TabsTrigger>
                    </TabsList>

                    <TabsContent value="bigfive">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Radar Chart */}
                            <Card className="bento-card">
                                <CardHeader>
                                    <CardTitle className="font-heading">Big Five Personality</CardTitle>
                                    <CardDescription>OCEAN model personality traits</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <RadarChart data={bigFiveData}>
                                                <PolarGrid stroke="#e5e7eb" />
                                                <PolarAngleAxis
                                                    dataKey="trait"
                                                    tick={{ fontSize: 12, fill: '#78716c' }}
                                                />
                                                <PolarRadiusAxis
                                                    angle={30}
                                                    domain={[0, 1]}
                                                    tick={{ fontSize: 10, fill: '#a8a29e' }}
                                                />
                                                <Radar
                                                    name="Traits"
                                                    dataKey="value"
                                                    stroke="#4F46E5"
                                                    fill="#4F46E5"
                                                    fillOpacity={0.3}
                                                />
                                            </RadarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Trait Breakdown */}
                            <Card className="bento-card">
                                <CardHeader>
                                    <CardTitle className="font-heading">Trait Breakdown</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <TraitBar label="Openness" value={profile?.big_five?.openness || 0} color="bg-indigo-500" />
                                    <TraitBar label="Conscientiousness" value={profile?.big_five?.conscientiousness || 0} color="bg-emerald-500" />
                                    <TraitBar label="Extraversion" value={profile?.big_five?.extraversion || 0} color="bg-amber-500" />
                                    <TraitBar label="Agreeableness" value={profile?.big_five?.agreeableness || 0} color="bg-coral-DEFAULT" />
                                    <TraitBar label="Neuroticism" value={profile?.big_five?.neuroticism || 0} color="bg-purple-500" />
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    <TabsContent value="disc">
                        <Card className="bento-card">
                            <CardHeader>
                                <CardTitle className="font-heading">DISC Profile</CardTitle>
                                <CardDescription>Behavioral assessment across four dimensions</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={discData} layout="vertical">
                                            <XAxis
                                                type="number"
                                                domain={[0, 1]}
                                                tickFormatter={(val) => `${(val * 100).toFixed(0)}%`}
                                            />
                                            <YAxis dataKey="name" type="category" width={100} />
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (active && payload?.[0]) {
                                                        return (
                                                            <div className="glass p-3 rounded-xl">
                                                                <p className="text-sm font-medium">
                                                                    {payload[0].payload.name}: {(payload[0].value * 100).toFixed(0)}%
                                                                </p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                                {discData.map((entry, index) => (
                                                    <Cell key={index} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="insights">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Strengths */}
                            <Card className="bento-card">
                                <CardHeader>
                                    <CardTitle className="font-heading flex items-center gap-2">
                                        <Zap className="w-5 h-5 text-emerald-500" />
                                        Your Strengths
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-3">
                                        {(profile?.strengths || ['Communication', 'Teamwork']).map((strength, i) => (
                                            <li key={i} className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                                                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                                                </div>
                                                <span className="text-stone-900">{strength}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>

                            {/* Growth Areas */}
                            <Card className="bento-card">
                                <CardHeader>
                                    <CardTitle className="font-heading flex items-center gap-2">
                                        <Target className="w-5 h-5 text-amber-500" />
                                        Growth Areas
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-3">
                                        {(profile?.growth_areas || ['Strategic thinking']).map((area, i) => (
                                            <li key={i} className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                                                    <Target className="w-4 h-4 text-amber-600" />
                                                </div>
                                                <span className="text-stone-900">{area}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>

                            {/* Team Compatibility */}
                            <Card className="md:col-span-2 bento-card">
                                <CardHeader>
                                    <CardTitle className="font-heading flex items-center gap-2">
                                        <Users className="w-5 h-5 text-indigo-500" />
                                        Team Compatibility
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-6">
                                        <div className="flex-1">
                                            <div className="flex justify-between text-sm mb-2">
                                                <span className="text-stone-600">Compatibility Score</span>
                                                <span className="font-mono font-medium text-stone-900">
                                                    {((profile?.team_compatibility_score || 0) * 100).toFixed(0)}%
                                                </span>
                                            </div>
                                            <Progress value={(profile?.team_compatibility_score || 0) * 100} className="h-3" />
                                        </div>
                                        <div className="text-sm text-stone-500 max-w-xs">
                                            {profile?.team_compatibility_score > 0 
                                                ? "Based on your psychometric profile, you work well in collaborative environments and can adapt to different team dynamics."
                                                : "Complete more activities to generate your team compatibility score."}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </Tabs>
            </motion.div>
        </div>
    );
}
